import { useEffect, useCallback } from 'react';
import { useZunelo } from '../context/ZuneloContext';
import { ViewMode } from '../domain/types';
import { ActiveToolType } from '../features/modals/ToolDrawerModal';
import {
  ModalFlags,
  ShortcutAction,
  isTypingInInput,
  modsFromKeyboardEvent,
  resolveShortcut,
} from './keyboardShortcutLogic';
import { useKeyboardShortcutSettings } from './keyboardShortcutSettings';

export { isTypingInInput } from './keyboardShortcutLogic';

export interface UseKeyboardShortcutsOptions {
  isSidebarExpanded?: boolean;
  setIsSidebarExpanded?:
    | React.Dispatch<React.SetStateAction<boolean>>
    | ((val: boolean | ((prev: boolean) => boolean)) => void);
  activeTool?: ActiveToolType;
  setActiveTool?: (tool: ActiveToolType) => void;
  view?: ViewMode;
  setView?: (view: ViewMode) => void;
  selectItem?: (id: string | null) => void;
  searchTarget?: string | null;
  setSearchTarget?: (target: string | null) => void;
  isMergeModalOpen?: boolean;
  setIsMergeModalOpen?: (open: boolean) => void;
  isDailyConfirmOpen?: boolean;
  setIsDailyConfirmOpen?: (open: boolean) => void;
  isDrawerOpen?: boolean;
  onCloseDrawer?: () => void;
  onNavigate?: () => void;
  enabled?: boolean;
  userId?: string;
}

export interface ShortcutDispatchDeps {
  setSearchTarget: (target: string | null) => void;
  setActiveTool?: (tool: ActiveToolType) => void;
  setIsMergeModalOpen: (open: boolean) => void;
  setIsDailyConfirmOpen: (open: boolean) => void;
  onCloseDrawer?: () => void;
  onNavigate?: () => void;
  setView: (view: ViewMode) => void;
  selectItem: (id: string | null) => void;
  setIsSidebarExpanded?:
    | React.Dispatch<React.SetStateAction<boolean>>
    | ((val: boolean | ((prev: boolean) => boolean)) => void);
  flags: ModalFlags;
}

function clearSearchAndTool(deps: ShortcutDispatchDeps): void {
  if (deps.flags.searchTarget !== null) {
    deps.setSearchTarget(null);
  }
  if (deps.flags.activeTool !== null) {
    deps.setActiveTool?.(null);
  }
}

function clearDialogs(deps: ShortcutDispatchDeps): void {
  if (deps.flags.isMergeModalOpen) {
    deps.setIsMergeModalOpen(false);
  }
  if (deps.flags.isDailyConfirmOpen) {
    deps.setIsDailyConfirmOpen(false);
  }
  if (deps.flags.isDrawerOpen) {
    deps.onCloseDrawer?.();
  }
}

function applyCloseModals(deps: ShortcutDispatchDeps): void {
  clearSearchAndTool(deps);
  clearDialogs(deps);
}

function applyOpenSearch(deps: ShortcutDispatchDeps): void {
  deps.onNavigate?.();
  deps.setSearchTarget('global');
}

function applyNavigate(deps: ShortcutDispatchDeps, view: ViewMode): void {
  deps.onNavigate?.();
  deps.setView(view);
  deps.selectItem(null);
}

function applyOpenTool(deps: ShortcutDispatchDeps, tool: NonNullable<ActiveToolType>): void {
  deps.onNavigate?.();
  deps.setActiveTool?.(tool);
}

function applyToggleSidebar(deps: ShortcutDispatchDeps): void {
  deps.setIsSidebarExpanded?.((prev) => !prev);
}

type ShortcutApplier = (action: ShortcutAction, deps: ShortcutDispatchDeps) => void;

const SHORTCUT_APPLIERS: Record<ShortcutAction['kind'], ShortcutApplier> = {
  'open-search': (_action, deps) => applyOpenSearch(deps),
  'close-modals': (_action, deps) => applyCloseModals(deps),
  navigate: (action, deps) => {
    if (action.kind === 'navigate') applyNavigate(deps, action.view);
  },
  'open-tool': (action, deps) => {
    if (action.kind === 'open-tool') applyOpenTool(deps, action.tool);
  },
  'toggle-sidebar': (_action, deps) => applyToggleSidebar(deps),
};

/**
 * Applies a resolved shortcut action to the provided dispatch dependencies.
 */
export function applyShortcutAction(action: ShortcutAction, deps: ShortcutDispatchDeps): void {
  SHORTCUT_APPLIERS[action.kind](action, deps);
}

interface ResolvedShortcutBindings {
  setView: (view: ViewMode) => void;
  selectItem: (id: string | null) => void;
  searchTarget: string | null;
  setSearchTarget: (target: string | null) => void;
  isMergeModalOpen: boolean;
  setIsMergeModalOpen: (open: boolean) => void;
  isDailyConfirmOpen: boolean;
  setIsDailyConfirmOpen: (open: boolean) => void;
  setIsSidebarExpanded?: UseKeyboardShortcutsOptions['setIsSidebarExpanded'];
  activeTool: ActiveToolType;
  setActiveTool?: (tool: ActiveToolType) => void;
  isDrawerOpen: boolean;
  onCloseDrawer?: () => void;
  onNavigate?: () => void;
  enabled: boolean;
}

function pickOr<T>(override: T | undefined, fallback: T): T {
  if (override !== undefined) return override;
  return fallback;
}

function resolveShortcutBindings(
  options: UseKeyboardShortcutsOptions,
  ctx: ReturnType<typeof useZunelo>
): ResolvedShortcutBindings {
  return {
    setView: pickOr(options.setView, ctx.setView),
    selectItem: pickOr(options.selectItem, ctx.selectItem),
    searchTarget: pickOr(options.searchTarget, ctx.searchTarget),
    setSearchTarget: pickOr(options.setSearchTarget, ctx.setSearchTarget),
    isMergeModalOpen: pickOr(options.isMergeModalOpen, ctx.isMergeModalOpen),
    setIsMergeModalOpen: pickOr(options.setIsMergeModalOpen, ctx.setIsMergeModalOpen),
    isDailyConfirmOpen: pickOr(options.isDailyConfirmOpen, ctx.isDailyConfirmOpen),
    setIsDailyConfirmOpen: pickOr(options.setIsDailyConfirmOpen, ctx.setIsDailyConfirmOpen),
    setIsSidebarExpanded: options.setIsSidebarExpanded,
    activeTool: pickOr(options.activeTool, null),
    setActiveTool: options.setActiveTool,
    isDrawerOpen: pickOr(options.isDrawerOpen, false),
    onCloseDrawer: options.onCloseDrawer,
    onNavigate: options.onNavigate,
    enabled: pickOr(options.enabled, true),
  };
}

/**
 * Global keyboard shortcuts hook for Zunelo.
 *
 * Supported shortcuts:
 * - ⌘K / Ctrl+K: Open SearchModal (searchTarget = 'global')
 * - Escape: Close any open modal (SearchModal, ToolDrawerModal, MergeViewModal, DailyConfirmModal)
 * - ⌘1 / 1 (when not typing): Navigate to 'radar'
 * - ⌘2 / 2 (when not typing): Navigate to 'jira'
 * - ⌘3 / 3 (when not typing): Navigate to 'slack'
 * - ⌘4 / 4 (when not typing): Navigate to 'github'
 * - ⌘5 / 5 (when not typing): Navigate to 'agents'
 * - ⌘D / Alt+D: Open Daily tool
 * - ⌘R / Alt+R: Open Reminders tool
 * - ⌘N / Alt+N: Open Notes tool
 * - ⌘B / [ (when not typing): Toggle sidebar expanded / collapsed
 */
export const useKeyboardShortcuts = (options: UseKeyboardShortcutsOptions = {}) => {
  const { bindings } = useKeyboardShortcutSettings(options.userId);
  const {
    setView,
    selectItem,
    searchTarget,
    setSearchTarget,
    isMergeModalOpen,
    setIsMergeModalOpen,
    isDailyConfirmOpen,
    setIsDailyConfirmOpen,
    setIsSidebarExpanded,
    activeTool,
    setActiveTool,
    isDrawerOpen,
    onCloseDrawer,
    onNavigate,
    enabled,
  } = resolveShortcutBindings(options, useZunelo());

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const flags: ModalFlags = {
        searchTarget,
        activeTool,
        isMergeModalOpen,
        isDailyConfirmOpen,
        isDrawerOpen,
      };
      const action = resolveShortcut(
        modsFromKeyboardEvent(e, isTypingInInput(e.target)),
        flags,
        enabled,
        bindings,
      );
      if (!action) return;
      e.preventDefault();
      applyShortcutAction(action, {
        setSearchTarget,
        setActiveTool,
        setIsMergeModalOpen,
        setIsDailyConfirmOpen,
        onCloseDrawer,
        onNavigate,
        setView,
        selectItem,
        setIsSidebarExpanded,
        flags,
      });
    },
    [
      enabled,
      bindings,
      searchTarget,
      activeTool,
      isMergeModalOpen,
      isDailyConfirmOpen,
      isDrawerOpen,
      onCloseDrawer,
      onNavigate,
      setSearchTarget,
      setActiveTool,
      setIsMergeModalOpen,
      setIsDailyConfirmOpen,
      setView,
      selectItem,
      setIsSidebarExpanded,
    ]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};
